import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Phone, MessageCircle, MapPin, Calendar, Loader2, ArrowUpRight,
  Check,
} from 'lucide-react';
import { dealerPublic, buildImageUrl } from '../../services/dealerApi';
import { sortedPhotos, heroPhotoUrl } from '../../utils/dealerPhoto';
import ListingFilters, { useListingFilters } from './ListingFilters';
import BuyerLeadModal from '../../components/dealers/BuyerLeadModal';
import ShareLinkButton from '../../components/dealers/ShareLinkButton';
import { useBuyerLead } from '../../hooks/useBuyerLead';
import SEO from '../../components/SEO';



// Makes that qualify a listing for the golden "Luxury" gloss badge.
// Kept as a constant so we can extend it without touching the card.
const LUXURY_MAKES = new Set([
  'Mercedes-Benz', 'Mercedes Benz', 'Mercedes', 'BMW', 'Audi', 'Porsche',
  'Jaguar', 'Land Rover', 'Range Rover', 'Volvo', 'Lexus', 'Mini',
  'Bentley', 'Rolls-Royce', 'Rolls Royce', 'Maserati', 'Ferrari',
  'Lamborghini', 'Aston Martin', 'Genesis', 'Cadillac', 'Infiniti',
  'Maybach', 'McLaren',
]);

const isLuxuryListing = (l) => {
  const make = (l?.ai_details?.make || '').trim();
  if (!make) return false;
  return LUXURY_MAKES.has(make) || Array.from(LUXURY_MAKES).some((m) => m.toLowerCase() === make.toLowerCase());
};

/* -------------------- Listing Card (Editorial / Cinematic) -------------------- */
const ListingCard = ({ l, slug, dark = false, variant = 'light' }) => {
  const hero = heroPhotoUrl(l.photos);
  const photos = sortedPhotos(l.photos || []);
  const enhancedCount = photos.filter((p) => p.is_enhanced).length;
  const luxury = isLuxuryListing(l);

  let skin;
  if (dark) {
    skin = {
      wrap: 'bg-[#14171D] border-[#272E3D] hover:border-[#D4AF37]/60',
      title: 'text-white',
      meta: 'text-slate-400',
      priceLabel: 'text-slate-500',
      price: 'text-white',
      cta: 'text-[#D4AF37]',
      divider: 'border-[#272E3D]',
    };
  } else if (variant === 'luxury') {
    // Cream card, dark cocoa text, burgundy accents for price/CTA
    skin = {
      wrap: 'bg-[#FCF7ED] border-[#D9C89A] hover:border-[#6C1D1D]',
      title: 'text-[#1A1512]',
      meta: 'text-[#6B5B3E]',
      priceLabel: 'text-[#8A7A5B]',
      price: 'text-[#6C1D1D]',
      cta: 'text-[#6C1D1D]',
      divider: 'border-[#E4D6AF]',
    };
  } else {
    skin = {
      wrap: 'bg-white border-zinc-200 hover:border-[#0A3622]',
      title: 'text-zinc-900',
      meta: 'text-zinc-500',
      priceLabel: 'text-zinc-400',
      price: 'text-zinc-900',
      cta: 'text-[#0A3622]',
      divider: 'border-zinc-100',
    };
  }

  const specBits = [
    l.year,
    l.fuel_type,
    l.ai_details?.transmission,
    l.km_driven ? `${l.km_driven.toLocaleString('en-IN')} km` : null,
  ].filter(Boolean).join(' · ');

  return (
    <Link
      to={`/dealers/${slug}/cars/${l.slug || l.id}`}
      data-testid={`public-listing-${l.id}`}
      className={`group block border ${skin.wrap} ${luxury ? 'ring-1 ring-[#D4AF37]/40 hover:ring-[#D4AF37]/70' : ''} rounded-sm overflow-hidden transition-colors duration-300`}
    >
      <div className="relative aspect-[3/2] overflow-hidden bg-zinc-100">
        {hero && (
          <img
            src={hero}
            alt={`${l.year || ''} ${l.ai_details?.make || ''} ${l.ai_details?.model || ''} for sale`.trim()}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
          />
        )}
        {/* Bottom shading for legibility */}
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
        {enhancedCount > 0 && (
          <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-2.5 py-1 bg-black/70 backdrop-blur text-[10px] font-mono-tech uppercase tracking-[0.2em] text-white">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Studio
          </span>
        )}
        {luxury && (
          <span
            className="luxury-badge absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-mono-tech uppercase tracking-[0.26em] text-[#3A2A05] font-bold"
            data-testid={`luxury-badge-${l.id}`}
          >
            <span className="luxury-badge__spark" aria-hidden="true">◆</span>
            Luxury
          </span>
        )}
        {photos.length > 1 && (
          <span className="absolute bottom-4 right-4 px-2.5 py-1 bg-black/70 text-white text-[10px] font-mono-tech tracking-wider">
            +{photos.length - 1}
          </span>
        )}
      </div>
      <div className="p-6 md:p-8 lg:p-10">
        <h3 className={`font-display text-2xl md:text-3xl lg:text-4xl font-bold leading-[1.1] tracking-tight ${skin.title}`}>
          {l.ai_details?.make} {l.ai_details?.model}
        </h3>
        <p className={`mt-3 text-[11px] md:text-xs uppercase tracking-[0.22em] ${skin.meta}`}>
          {specBits}
        </p>

        <div className={`mt-7 md:mt-9 pt-6 border-t ${skin.divider} flex items-end justify-between gap-3`}>
          <div className="min-w-0 flex-1">
            <div className={`text-[10px] uppercase tracking-[0.24em] font-mono-tech mb-1 ${skin.priceLabel}`}>Price</div>
            <div className={`font-display text-3xl md:text-4xl font-black tracking-tight leading-none ${skin.price} truncate`}>
              ₹{(l.price || 0).toLocaleString('en-IN')}
            </div>
          </div>
          <span className={`shrink-0 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.22em] ${skin.cta} pb-1`}>
            View <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        </div>
      </div>
    </Link>
  );
};

const InventoryWithFilters = ({ listings, slug, dark = false, variant = 'light' }) => {
  const { filters, filtered, toggle, clearAll, activeCount, query, setQuery } = useListingFilters(listings);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[220px,minmax(0,1fr)] gap-6 lg:gap-8">
      <ListingFilters
        filters={filters}
        toggle={toggle}
        clearAll={clearAll}
        activeCount={activeCount}
        total={listings.length}
        filteredCount={filtered.length}
        dark={dark}
        query={query}
        setQuery={setQuery}
      />
      <div>
        {filtered.length === 0 ? (
          <div className={`p-16 text-center ${dark ? 'bg-[#14171D] border border-[#272E3D] text-slate-400' : 'bg-white border border-zinc-200 text-zinc-500'}`}>
            <p className="font-display text-lg font-bold mb-1">Nothing matches your filters.</p>
            {activeCount > 0 && (
              <button onClick={clearAll} className={`text-xs uppercase tracking-[0.2em] font-bold ${dark ? 'text-[#D4AF37]' : 'text-[#0A3622]'} hover:underline`}>
                Clear all
              </button>
            )}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-6 lg:gap-10">
            {filtered.map((l) => <ListingCard key={l.id} l={l} slug={slug} dark={dark} variant={variant} />)}
          </div>
        )}
      </div>
    </div>
  );
};

/* ============================== MAIN PAGE ============================== */
const PublicDealerSite = () => {
  const { slug: paramSlug } = useParams();
  // When the app is served on a dealer's custom domain, App.js stashes the
  // resolved dealer slug on window.__DEALER_HOST_SLUG__ so this page can
  // render at path `/` without a URL parameter.
  const slug = paramSlug || (typeof window !== 'undefined' && window.__DEALER_HOST_SLUG__) || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dealerPublic.site(slug)
      .then((res) => setData(res))
      .catch(() => setData({ notFound: true }))
      .finally(() => setLoading(false));
  }, [slug]);

  // Hooks must be unconditional.
  const lead = useBuyerLead({ slug, dealer: data?.dealer });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#0A3622] animate-spin" />
      </div>
    );
  }
  if (!data || data.notFound) {
    return (
      <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center text-zinc-700">
        <div className="text-center">
          <h1 className="font-display text-3xl font-extrabold mb-2">Dealer not found</h1>
          <Link to="/" className="text-sm uppercase tracking-[0.2em] font-bold text-[#0A3622] hover:underline">Go home</Link>
        </div>
      </div>
    );
  }

  const { dealer, listings } = data;
  const isDark = dealer.template === 'premium_dark';
  const onWhatsApp = () => lead.openFor({ action: 'whatsapp' });
  const onCall = () => lead.openFor({ action: 'call' });

  if (isDark) {
    return (
      <>
        <DealerSiteSEO dealer={dealer} listings={listings} slug={slug} />
        <CinematicDark dealer={dealer} listings={listings} slug={slug} onWhatsApp={onWhatsApp} onCall={onCall} />
        <BuyerLeadModal {...lead.modalProps} />
      </>
    );
  }
  return (
    <>
      <DealerSiteSEO dealer={dealer} listings={listings} slug={slug} />
      <EditorialLight dealer={dealer} listings={listings} slug={slug} onWhatsApp={onWhatsApp} onCall={onCall} template={dealer.template} />
      <BuyerLeadModal {...lead.modalProps} />
    </>
  );
};

// ============================== SEO ==============================
const DealerSiteSEO = ({ dealer, listings, slug }) => {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const canonical = `${origin}/dealers/${slug}`;
  const cityPart = dealer.city ? ` in ${dealer.city}` : '';
  // Dealer-first branding — no "| Propitstop" suffix on tab title or share
  // cards. Google, LinkedIn, WhatsApp previews all inherit `og:title`.
  const title = `${dealer.name} — Used Cars${cityPart}`.slice(0, 70);
  const desc = `Browse ${listings.length} verified pre-owned cars at ${dealer.name}${dealer.city ? ' in ' + dealer.city : ''}. ${dealer.tagline || 'Quality assured. Inspected. Ready to drive.'}`.slice(0, 200);
  const logoAbs = dealer.logo_url
    ? (dealer.logo_url.startsWith('http') ? dealer.logo_url : buildImageUrl(dealer.logo_url))
    : null;
  const ogImage = dealer.hero_image_url
    ? (dealer.hero_image_url.startsWith('http') ? dealer.hero_image_url : buildImageUrl(dealer.hero_image_url))
    : (logoAbs || 'https://images.unsplash.com/photo-1583489320541-03aa6b62987c?auto=format&fit=crop&w=1200&q=70');

  const dealerJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AutoDealer',
    name: dealer.name,
    url: canonical,
    ...(dealer.tagline ? { description: dealer.tagline } : {}),
    ...(logoAbs ? { logo: logoAbs } : {}),
    ...(dealer.contact_number || dealer.whatsapp_number ? { telephone: dealer.contact_number || dealer.whatsapp_number } : {}),
    ...(dealer.address || dealer.city ? {
      address: {
        '@type': 'PostalAddress',
        ...(dealer.address ? { streetAddress: dealer.address } : {}),
        ...(dealer.city ? { addressLocality: dealer.city } : {}),
        addressCountry: 'IN',
      }
    } : {}),
    ...(dealer.year_of_inception ? { foundingDate: String(dealer.year_of_inception) } : {}),
  };

  const itemListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `Available cars at ${dealer.name}`,
    numberOfItems: listings.length,
    itemListElement: listings.slice(0, 30).map((l, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `${origin}/dealers/${slug}/cars/${l.slug || l.id}`,
      name: `${l.year || ''} ${l.ai_details?.make || ''} ${l.ai_details?.model || ''}`.trim(),
    })),
  };

  return <SEO title={title} description={desc} canonical={canonical} image={ogImage} favicon={logoAbs} jsonLd={[dealerJsonLd, itemListJsonLd]} />;
};

/* -------------------- Editorial Light (clean_white / marketplace) -------------------- */
const DEFAULT_HERO = 'https://images.unsplash.com/photo-1583489320541-03aa6b62987c?auto=format&fit=crop&w=1920&q=70';

const PALETTES = {
  clean_white: {
    bodyBg: 'bg-[#F4F4F5]',
    accent: '#0A3622',
    accentHover: '#072718',
    gold: '#D4AF37',
    cardBg: 'bg-white',
    trustBg: 'bg-white',
    border: 'border-zinc-200',
  },
  // Luxury — cream / warm ivory body, deep burgundy accents, tasteful gold.
  // Evokes the boutique showroom feel (see luxury reference in requirements).
  luxury: {
    bodyBg: 'bg-[#F1E7CE]',          // warm ivory / cream
    accent: '#6C1D1D',                // deep burgundy
    accentHover: '#4E1010',
    gold: '#B8860B',                  // dark goldenrod — pairs with burgundy
    cardBg: 'bg-[#FCF7ED]',           // slightly whiter cream card
    trustBg: 'bg-[#F7EDD3]',
    border: 'border-[#D9C89A]',       // warm sandy hairline
  },
};

// Backwards compat: dealers who previously chose the silver "marketplace"
// template are silently promoted to the new Luxury palette (per the product
// direction to replace Silver with Luxury).
PALETTES.marketplace = PALETTES.luxury;

const EditorialLight = ({ dealer, listings, slug, onWhatsApp, onCall, template = 'clean_white' }) => {
  const palette = PALETTES[template] || PALETTES.clean_white;
  const heroVideo = dealer.hero_video_url ? buildImageUrl(dealer.hero_video_url) : null;
  const heroBg = dealer.hero_image_url ? buildImageUrl(dealer.hero_image_url) : DEFAULT_HERO;
  return (
    <div data-dealer-skin data-template={template} className={`min-h-screen ${palette.bodyBg} text-zinc-900 overflow-x-hidden`}>
      {/* HERO: cinematic dark banner with dealer card anchored bottom-left */}
      <section className="relative overflow-hidden grain bg-zinc-900">
        {heroVideo ? (
          <video
            src={heroVideo}
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay
            loop
            muted
            playsInline
            poster={heroBg}
            data-testid="hero-video"
          />
        ) : (
          <img src={heroBg} alt="" fetchpriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
        )}
        {/* Subtle bottom-only fade so the dealer name stays legible over any
            hero image — no full-frame dimming any more. */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/70 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-12 lg:px-24 pt-20 sm:pt-28 md:pt-40 pb-12 sm:pb-16 md:pb-24">
          <div className="text-[10px] uppercase tracking-[0.4em] font-mono-tech mb-4 sm:mb-6" style={{ color: palette.gold }}>
            Curated Pre-Owned
          </div>
          <div className="grid md:grid-cols-[1fr,auto] items-end gap-6 md:gap-10">
            <div className="min-w-0">
              <h1 className="font-display text-4xl sm:text-5xl md:text-7xl lg:text-8xl text-white font-black leading-[0.95] tracking-[-0.04em] break-words">
                {dealer.name}
              </h1>
              {dealer.tagline && (
                <p className="mt-4 sm:mt-5 text-base sm:text-lg md:text-xl text-white/80 max-w-2xl leading-relaxed">
                  {dealer.tagline}
                </p>
              )}
              <div className="mt-5 sm:mt-7 flex flex-wrap gap-x-5 gap-y-2 text-[10px] sm:text-[11px] uppercase tracking-[0.18em] sm:tracking-[0.22em] font-mono-tech text-white/60">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-4 h-4 inline-flex items-center justify-center rounded-full border" style={{ borderColor: palette.gold, color: palette.gold }}>
                    <Check className="w-2.5 h-2.5" strokeWidth={3} />
                  </span>
                  Verified Dealer
                </span>
                {dealer.year_of_inception && (
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="w-3 h-3" /> Est. {dealer.year_of_inception}
                  </span>
                )}
                {dealer.city && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="w-3 h-3" /> {dealer.city}
                  </span>
                )}
              </div>
            </div>
            {dealer.logo_url && (
              <div className="hidden md:flex w-48 h-48 lg:w-56 lg:h-56 bg-white/95 backdrop-blur items-center justify-center p-5 ring-1 ring-white/20" data-testid="dealer-logo-desktop">
                <img src={buildImageUrl(dealer.logo_url)} alt={`${dealer.name} logo`} className="max-w-full max-h-full object-contain" />
              </div>
            )}
          </div>

          {/* Mobile logo — appears above hero content on small screens */}
          {dealer.logo_url && (
            <div className="md:hidden mt-6 inline-flex w-24 h-24 bg-white/95 backdrop-blur items-center justify-center p-3 ring-1 ring-white/20" data-testid="dealer-logo-mobile">
              <img src={buildImageUrl(dealer.logo_url)} alt={`${dealer.name} logo`} className="max-w-full max-h-full object-contain" />
            </div>
          )}

          <div className="mt-7 sm:mt-10 flex flex-wrap gap-3">
            <button
              onClick={onWhatsApp}
              data-testid="hero-whatsapp"
              className="group inline-flex items-center gap-3 px-5 sm:px-7 py-3.5 sm:py-4 bg-[#25D366] hover:bg-[#22C55E] text-white font-bold tracking-wide transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              <span className="text-xs sm:text-sm uppercase tracking-[0.18em] sm:tracking-[0.2em]">WhatsApp Us</span>
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            {dealer.contact_number && (
              <button
                onClick={onCall}
                className="group inline-flex items-center gap-3 px-5 sm:px-7 py-3.5 sm:py-4 bg-white/10 hover:bg-white/15 backdrop-blur text-white font-bold ring-1 ring-white/30 transition-colors"
              >
                <Phone className="w-4 h-4" />
                <span className="text-xs sm:text-sm uppercase tracking-[0.18em] sm:tracking-[0.2em]">Call Now</span>
              </button>
            )}
            <ShareLinkButton
              slug={slug}
              title={`${dealer.name} — Used Cars${dealer.city ? ' in ' + dealer.city : ''}`}
              text={`Browse pre-owned cars at ${dealer.name}${dealer.city ? ' in ' + dealer.city : ''}.`}
              variant="dark"
              label="Share Page"
              testId="hero-share-dealer"
            />
          </div>
        </div>
      </section>

      {/* INVENTORY */}
      <section className="max-w-[1440px] mx-auto px-5 sm:px-6 md:px-10 lg:px-12 py-14 sm:py-20 md:py-28">
        <div className="flex items-end justify-between mb-8 sm:mb-10 md:mb-14 gap-6 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] font-mono-tech mb-2" style={{ color: palette.accent }}>
              The Collection
            </div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tight">
              Available Cars
            </h2>
          </div>
          <div className="text-right">
            <div className="font-display text-2xl sm:text-3xl font-black">{listings.length}</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-mono-tech">
              vehicles ready
            </div>
          </div>
        </div>
        {listings.length === 0 ? (
          <div className={`p-16 ${palette.cardBg} border ${palette.border} text-center`}>
            <p className="font-display text-xl font-bold text-zinc-800 mb-1">No cars listed yet.</p>
            <p className="text-sm text-zinc-500">Check back soon — fresh stock arrives weekly.</p>
          </div>
        ) : (
          <InventoryWithFilters
            listings={listings}
            slug={slug}
            variant={template === 'luxury' || template === 'marketplace' ? 'luxury' : 'light'}
          />
        )}
      </section>

      {/* TRUST */}
      <section className={`border-t ${palette.border} ${palette.trustBg}`}>
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 lg:px-24 py-12 sm:py-16 md:py-20 grid md:grid-cols-3 gap-8 md:gap-16">
          {[
            { kicker: '01', title: 'Verified Dealer', body: 'Identity & business verified by Propitstop. We only onboard trusted dealers.' },
            dealer.year_of_inception
              ? { kicker: '02', title: `Trusted Since ${dealer.year_of_inception}`, body: 'Years of building relationships, not just selling cars.' }
              : { kicker: '02', title: 'Trusted Network', body: 'Part of India\'s growing trusted dealer network.' },
            { kicker: '03', title: 'Powered by Propitstop', body: 'India\'s automotive trust platform — service, repair, inspection, all in one.' },
          ].map((b) => (
            <div key={b.title}>
              <div className="font-mono-tech text-xs uppercase tracking-[0.3em] mb-3" style={{ color: palette.accent }}>{b.kicker}</div>
              <h3 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mb-2">{b.title}</h3>
              <p className="text-sm text-zinc-600 leading-relaxed">{b.body}</p>
            </div>
          ))}
        </div>
        <div className={`border-t ${palette.border} py-5 text-center`}>
          <span className="font-mono-tech text-[10px] uppercase tracking-[0.4em] text-zinc-400 transition-colors cursor-default">
            © {new Date().getFullYear()} {dealer.name} — Powered by Propitstop
          </span>
        </div>
      </section>

      {/* Sticky WhatsApp pill — lifted above the Emergent badge */}
      <button
        onClick={onWhatsApp}
        data-testid="sticky-whatsapp"
        className="fixed bottom-20 right-4 sm:right-6 z-40 inline-flex items-center gap-2 px-4 sm:px-5 py-3 bg-[#25D366] hover:bg-[#22C55E] text-white shadow-2xl shadow-black/20 group"
      >
        <span className="absolute inset-0 bg-[#25D366] opacity-0 group-hover:opacity-30 animate-ping -z-10" />
        <MessageCircle className="w-5 h-5" />
        <span className="hidden sm:inline text-xs font-bold uppercase tracking-[0.2em]">Chat with us</span>
      </button>
    </div>
  );
};

/* -------------------- Cinematic Dark (premium_dark) -------------------- */
const CinematicDark = ({ dealer, listings, slug, onWhatsApp, onCall }) => {
  const heroVideo = dealer.hero_video_url ? buildImageUrl(dealer.hero_video_url) : null;
  const heroBg = dealer.hero_image_url ? buildImageUrl(dealer.hero_image_url) : DEFAULT_HERO;
  return (
    <div data-dealer-skin className="min-h-screen bg-[#0C0E12] text-slate-200 overflow-x-hidden">
      <section className="relative overflow-hidden grain">
        {heroVideo ? (
          <video
            src={heroVideo}
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay
            loop
            muted
            playsInline
            poster={heroBg}
            data-testid="hero-video"
          />
        ) : (
          <img src={heroBg} alt="" fetchpriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
        )}
        {/* Bottom-only fade — media reads crisp, headline stays legible. */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0C0E12] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-12 lg:px-24 pt-20 sm:pt-28 md:pt-40 pb-14 sm:pb-20">
          {dealer.logo_url && (
            <div
              className="mb-6 sm:mb-8 inline-flex w-24 h-24 sm:w-28 sm:h-28 md:w-36 md:h-36 bg-white/95 items-center justify-center p-3 sm:p-4 ring-1 ring-white/15"
              data-testid="dealer-logo-cinematic"
            >
              <img
                src={buildImageUrl(dealer.logo_url)}
                alt={`${dealer.name} logo`}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          )}
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-mono-tech mb-4 sm:mb-6">
            Premium Pre-Owned
          </div>
          <h1 className="font-display text-4xl sm:text-5xl md:text-7xl lg:text-8xl text-white font-black leading-[0.95] tracking-[-0.04em] break-words">
            {dealer.name}
          </h1>
          {dealer.tagline && (
            <p className="mt-4 sm:mt-5 text-base sm:text-lg md:text-xl text-slate-300 max-w-2xl leading-relaxed">
              {dealer.tagline}
            </p>
          )}
          <div className="mt-7 sm:mt-10 flex flex-wrap gap-3">
            <button
              onClick={onWhatsApp}
              data-testid="hero-whatsapp"
              className="inline-flex items-center gap-3 px-5 sm:px-7 py-3.5 sm:py-4 bg-[#D4AF37] hover:bg-[#C29B27] text-[#0C0E12] font-bold transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              <span className="text-xs sm:text-sm uppercase tracking-[0.18em] sm:tracking-[0.2em]">WhatsApp Us</span>
            </button>
            {dealer.contact_number && (
              <button
                onClick={onCall}
                className="inline-flex items-center gap-3 px-5 sm:px-7 py-3.5 sm:py-4 bg-white/5 hover:bg-white/10 backdrop-blur text-white font-bold ring-1 ring-white/20 transition-colors"
              >
                <Phone className="w-4 h-4" />
                <span className="text-xs sm:text-sm uppercase tracking-[0.18em] sm:tracking-[0.2em]">Call Now</span>
              </button>
            )}
            <ShareLinkButton
              slug={slug}
              title={`${dealer.name} — Used Cars${dealer.city ? ' in ' + dealer.city : ''}`}
              text={`Browse pre-owned cars at ${dealer.name}${dealer.city ? ' in ' + dealer.city : ''}.`}
              variant="dark"
              label="Share Page"
              testId="hero-share-dealer"
              className="!bg-white/5 hover:!bg-white/10 !ring-white/20"
            />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 lg:px-24 py-14 sm:py-20 md:py-28">
        <div className="mb-8 sm:mb-10 flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-mono-tech mb-2">The Collection</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-black text-white tracking-tight">Available Cars</h2>
          </div>
          <div className="text-right">
            <div className="font-display text-2xl sm:text-3xl font-black text-white">{listings.length}</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-mono-tech">vehicles ready</div>
          </div>
        </div>
        <InventoryWithFilters listings={listings} slug={slug} dark />
      </section>

      <button
        onClick={onWhatsApp}
        data-testid="sticky-whatsapp"
        className="fixed bottom-20 right-4 sm:right-6 z-40 inline-flex items-center gap-2 px-4 sm:px-5 py-3 bg-[#25D366] hover:bg-[#22C55E] text-white shadow-2xl shadow-black/40"
      >
        <MessageCircle className="w-5 h-5" />
        <span className="hidden sm:inline text-xs font-bold uppercase tracking-[0.2em]">Chat with us</span>
      </button>
    </div>
  );
};

export default PublicDealerSite;
